
import { useEffect, useRef, useState } from 'react';
import {
  BuildingsIcon,
  CheckIcon,
  GearIcon,
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityStore } from '@/store/entity';
import { useOrganizationStore } from '@/store/organization.store';
import { useAuth } from '@/store/auth.store';
import { useTaskState } from '@/store/taskState';
import { useTodayLayout } from '@/store/today-layout.store';
import { useUserProfileStore } from '@/store/user-profile.store';
import { useUserPreferencesStore } from '@/store/user-preferences.store';
import { Button } from '@/components/ui/button';
import { OrgAvatar } from '@/components/ui/org-avatar';
import { Badge } from '@/components/ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { AccountType, Organization, ExternalOrgRelationship } from '@scrambl/shared';
import { clearOrganizationContextCache } from '@/lib/api/api-client';
import { useOrganizationsQuery, ExtendedOrganization } from '@/hooks/queries/useOrganizationsQuery';
import { useMyHostOrgsQuery } from '@/hooks/queries/useExternalOrgsQuery';
import { useMyEntities } from '@/hooks/useMyEntities';
import { invalidateSubscriptionCache } from '@/hooks/useOrganizationSubscription';
import { queryKeys } from '@/hooks/query-keys';
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/layout/OrganizationSwitcher');

type OrgData = Organization;

// Extended type for display purposes
interface DisplayOrganization extends Organization {
  isExternal?: boolean;
  relationshipId?: string;
  logoUrl?: string | null;
  // Store the full relationship for permission checking
  externalRelationship?: ExternalOrgRelationship;
}

interface OrganizationSwitcherProps {
  className?: string;
  compact?: boolean;
}

// Async function to clear all organization-specific stores
function clearOrganizationSpecificStores() {
  try {
    // Clear entity store
    const entityStore = useEntityStore.getState();
    if (typeof entityStore.reset === 'function') {
      entityStore.reset();
    }

    // Deal state is managed by TanStack Query - cache invalidation handles cleanup

    // Lead state is managed by TanStack Query - cache invalidation handles cleanup

    // Project state is managed by TanStack Query - cache invalidation handles cleanup

    const taskStore = useTaskState.getState();
    // Call the full reset method to clear all task data
    try {
      if (typeof taskStore.reset === 'function') {
        taskStore.reset();
      }
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not reset task store:', error);
    }

    // Clear task selection store
    try {
      if (typeof taskStore.clearSelection === 'function') {
        taskStore.clearSelection();
      }
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not clear task selection store:', error);
    }

    // Clear task name edit store
    try {
      if (typeof taskStore.setEditingTask === 'function') {
        taskStore.setEditingTask(null);
      }
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not clear task name edit store:', error);
    }

    // Clear dashboard layout store
    try {
      // Reset dashboard layout completely (clears layout and resets fetch flag)
      useTodayLayout.getState().reset();
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not clear dashboard layout store:', error);
    }
  } catch (error) {
    logger.warn('[OrganizationSwitcher] Error clearing some stores:', error);
  }
}

export function OrganizationSwitcher({ className, compact = false }: OrganizationSwitcherProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [forceRender, setForceRender] = useState(0);
  const hasInitializedRef = useRef(false);
  const logoFetchAttemptsRef = useRef<Record<string, boolean>>({});
  const isMobile = useIsMobile();

  const {
    currentOrganization,
    setCurrentOrganization,
  } = useOrganizationStore();

  const { resetOrganizationData } = useUserProfileStore();
  const { isAuthenticated } = useAuth();
  const { clearPreferences: clearUserPreferences } = useUserPreferencesStore();

  // Check if organizations data is already in cache (from MainLayout)
  // This avoids triggering a duplicate fetch - MainLayout is the primary fetcher
  const cachedOrganizations = queryClient.getQueryData<ExtendedOrganization[]>(
    queryKeys.organizations.list({ withLogos: true })
  );

  // Only enable the query if there's no cached data (fallback for edge cases)
  const { data: queryOrganizations = [] } = useOrganizationsQuery({
    enabled: isAuthenticated && !cachedOrganizations,
  });

  // Use cached data if available, otherwise use query data
  const organizations = cachedOrganizations || queryOrganizations;

  // Fetch external organizations (host orgs the current user can access)
  // This uses user-level access, not organization-level
  const { data: myHostOrgs = [], isLoading: isLoadingHostOrgs, error: hostOrgsError } = useMyHostOrgsQuery({
    enabled: isAuthenticated,
  });

  // Client portal entities the user has guest access to
  const { data: myEntitiesData } = useMyEntities();
  const portalEntities = myEntitiesData?.entities ?? [];

  useEffect(() => {
    // No need to fetch organizations here - they should already be fetched by the app initialization
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
    }
  }, [isAuthenticated, organizations.length]);

  // Convert host orgs to display format
  const externalOrganizations: DisplayOrganization[] = myHostOrgs.map((relationship) => ({
    id: relationship.hostOrganization?.id || relationship.hostOrganizationId,
    name: relationship.hostOrganization?.name || 'Unknown Organization',
    slug: relationship.hostOrganization?.slug || '',
    logo: relationship.hostOrganization?.logo || null,
    logoUrl: relationship.hostOrganization?.logo || null,
    isExternal: true,
    relationshipId: relationship.id,
    externalRelationship: relationship, // Store full relationship for permission checking
    createdAt: new Date(),
    updatedAt: new Date(),
  }));

  // Combine regular orgs (only those where the user has a real membership) with
  // external workspaces. Orgs the user can only reach as a client-portal guest
  // are surfaced separately in the "Client Portals" group below.
  const membershipOrgs = organizations.filter(org => Boolean(org.userMembership));
  const allOrganizations: DisplayOrganization[] = [
    ...membershipOrgs.map(org => ({ ...org, isExternal: false })),
    ...externalOrganizations.filter(
      extOrg => !membershipOrgs.some(org => org.id === extOrg.id)
    ),
  ];

  const handleOrgSelect = (org: DisplayOrganization) => {
    if (!org.id || !org.slug) {
      toast.error('Organization has no slug. Please contact support.');
      return;
    }

    // Clear all organization-specific caches and stores
    resetOrganizationData();
    clearUserPreferences();
    clearOrganizationSpecificStores();

    // Clear API client's organization context cache
    try {
      // Force clear the organization context cache in the API client
      clearOrganizationContextCache();
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not clear API client cache:', error);
    }

    // Clear subscription cache for the previous org
    try {
      if (currentOrganization?.id) {
        invalidateSubscriptionCache(currentOrganization.id);
      }
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not clear subscription cache:', error);
    }

    // Set the new organization (this will trigger new data fetching)
    // Preserve the isExternal flag as isExternalAccess in the organization store
    // Pass the external relationship for permission checking
    setCurrentOrganization(
      {
        ...org,
        isExternalAccess: org.isExternal === true,
      },
      org.isExternal ? org.externalRelationship : undefined
    );

    // Navigate to the new organization's dashboard
    // The OrgRouteGuard will handle subscription checking and redirect to subscription page if needed
    navigate(`/${org.slug}/today`);
    setOpen(false);
  };

  const handlePortalSelect = (portalEntityId: string) => {
    resetOrganizationData();
    clearUserPreferences();
    clearOrganizationSpecificStores();
    try {
      clearOrganizationContextCache();
    } catch (error) {
      logger.warn('[OrganizationSwitcher] Could not clear API client cache:', error);
    }
    navigate(`/client-portal/${portalEntityId}`);
    setOpen(false);
  };

  const handleGoToOrganizations = () => {
    navigate('/organizations');
    setOpen(false);
  };

  // Separate regular orgs from external orgs for grouped display
  const regularOrgs = allOrganizations.filter(org => !org.isExternal);
  const externalOrgs = allOrganizations.filter(org => org.isExternal);
  // Personal spaces get their own section at the top; this split is informative
  // for business users too (and a business user may own a personal space), so
  // it's not account-type gated.
  const personalOrgs = regularOrgs.filter(
    org => org.accountType === AccountType.PERSONAL,
  );
  const businessOrgs = regularOrgs.filter(
    org => org.accountType !== AccountType.PERSONAL,
  );

  // Shared row renderer for the Personal and Organizations groups (identical
  // markup — only the grouping heading differs).
  const renderOrgCommandItem = (org: DisplayOrganization) => (
    <CommandItem
      key={`${org.id}-${forceRender}`}
      value={org.name}
      onSelect={() => handleOrgSelect(org)}
      className="hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10 cursor-pointer rounded-lg px-2 py-2 transition-all duration-200 group"
    >
      <div className="flex items-center gap-2 flex-1">
        <OrgAvatar
          organization={org}
          logoUrl={org.logoUrl || undefined}
          size="xs"
          className="transition-all duration-200"
        />
        <span className="truncate font-medium text-foreground group-hover:text-primary transition-colors text-sm">{org.name}</span>
      </div>
      <CheckIcon
        className={cn(
          "ml-auto h-3.5 w-3.5 text-primary transition-all duration-200",
          currentOrganization?.id === org.id ? "opacity-100 scale-100" : "opacity-0 scale-75"
        )}
      />
    </CommandItem>
  );

  const renderPopoverContent = () => (
    <PopoverContent
      className="w-[280px] p-0 bg-background rounded-lg"
      align="start"
      side="bottom"
    >
      <Command className="bg-transparent">
        <CommandInput
          placeholder="Search spaces..."
          className="bg-background/50 focus:border-primary/50 transition-colors h-9 text-sm placeholder:text-muted-foreground/60"
        />
        <CommandList className="max-h-[320px] overflow-y-auto">
          <CommandEmpty className="py-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted/20">
                <BuildingsIcon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="font-medium text-foreground text-sm">No spaces found</p>
                <p className="text-xs text-muted-foreground mt-0.5">Try a different search term</p>
              </div>
            </div>
          </CommandEmpty>

          {/* Personal spaces */}
          {personalOrgs.length > 0 && (
            <CommandGroup heading="Personal" className="p-1">
              {personalOrgs.map((org) => renderOrgCommandItem(org))}
            </CommandGroup>
          )}

          {/* Regular Organizations */}
          {businessOrgs.length > 0 && (
            <>
              {personalOrgs.length > 0 && (
                <div className="h-px bg-border/50 mx-2" />
              )}
              <CommandGroup heading="Organizations" className="p-1">
                {businessOrgs.map((org) => renderOrgCommandItem(org))}
              </CommandGroup>
            </>
          )}

          {/* Client Portals */}
          {portalEntities.length > 0 && (
            <>
              <div className="h-px bg-border/50 mx-2" />
              <CommandGroup heading="Client Portals" className="p-1">
                {portalEntities.map((entity) => (
                  <CommandItem
                    key={`portal-${entity.entityId}`}
                    value={`${entity.organization.name} ${entity.entityName} client portal`}
                    onSelect={() => handlePortalSelect(entity.entityId)}
                    className="hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10 cursor-pointer rounded-lg px-2 py-2 transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <OrgAvatar
                        organization={entity.organization}
                        logoUrl={entity.organization.logoUrl}
                        size="xs"
                        className="transition-all duration-200 shrink-0"
                      />
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium text-foreground group-hover:text-primary transition-colors text-sm">
                          {entity.organization.name}
                        </span>
                        <span className="truncate text-xs text-muted-foreground">
                          {entity.entityName}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* External Organizations */}
          {externalOrgs.length > 0 && (
            <>
              <div className="h-px bg-border/50 mx-2" />
              <CommandGroup heading="External Workspaces" className="p-1">
                {externalOrgs.map((org) => (
                  <CommandItem
                    key={`external-${org.id}-${forceRender}`}
                    value={`${org.name} external`}
                    onSelect={() => handleOrgSelect(org)}
                    className="hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10 cursor-pointer rounded-lg px-2 py-2 transition-all duration-200 group"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="relative shrink-0">
                        <OrgAvatar
                          organization={org}
                          logoUrl={org.logoUrl || undefined}
                          size="xs"
                          className="transition-all duration-200"
                        />
                        {/* External indicator icon - building to indicate organization */}
                        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-blue-500 border border-background flex items-center justify-center">
                          <BuildingsIcon weight="fill" className="w-2 h-2 text-white" />
                        </div>
                      </div>
                      <span className="truncate font-medium text-foreground group-hover:text-primary transition-colors text-sm">{org.name}</span>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-1 h-3.5 w-3.5 text-primary transition-all duration-200 shrink-0",
                        currentOrganization?.id === org.id ? "opacity-100 scale-100" : "opacity-0 scale-75"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <div className="h-px bg-border/50 mx-2" />
          <CommandGroup className="p-1">
            <CommandItem
              onSelect={handleGoToOrganizations}
              className="cursor-pointer hover:!bg-primary/10 data-[highlighted]:!bg-primary/10 [&[aria-selected=true]]:!bg-primary/10 [&[data-selected=true]]:!bg-primary/10 text-sm text-foreground rounded-lg px-2 py-2 transition-all duration-200 group"
            >
              <div className="flex items-center justify-center w-5 h-5 rounded-lg bg-muted/20 group-hover:bg-primary/10 transition-all duration-200 mr-2">
                <GearIcon className="h-3 w-3" />
              </div>
              <span className="font-medium text-sm">Manage Spaces</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  );

  if (!isAuthenticated || (allOrganizations.length === 0 && portalEntities.length === 0)) {
    return null;
  }

  // Always use the same layout now that it's in the header, regardless of sidebar state
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "justify-start hover:!bg-primary/10 transition-colors duration-200 h-8 px-2",
            compact ? "h-6 px-1.5" : "",
            isMobile ? "w-full" : "max-w-[220px] w-auto",
            className
          )}
          key={`org-switcher-${forceRender}-${currentOrganization?.id || 'none'}`}
        >
          <div className={cn("flex items-center", compact ? "gap-1.5" : "gap-2")}>
            {currentOrganization ? (
              <>
                <OrgAvatar
                  organization={currentOrganization}
                  logoUrl={currentOrganization.logoUrl || undefined}
                  size="xs"
                  className={cn("transition-all duration-200 h-5 w-5", compact && "h-4 w-4")}
                />
                <span className={cn("truncate font-medium text-sm", compact && "text-xs")}>{currentOrganization.name}</span>
              </>
            ) : (
              <>
                <OrgAvatar
                  organization={null}
                  size="xs"
                  className={cn("bg-muted/20 h-5 w-5", compact && "h-4 w-4")}
                />
                <span className={cn("font-medium text-muted-foreground text-sm", compact && "text-xs")}>Select organization</span>
              </>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      {renderPopoverContent()}
    </Popover>
  );
}
